import React, { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { Checkbox, Collapse, IconButton, Tooltip, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, Button, Radio } from '@material-ui/core'
import { TuneOutlined, Layers, ExpandLess, ExpandMore, ShowChart, InfoOutlined, VerticalAlignCenterOutlined, Opacity } from '@material-ui/icons'

import { RootState } from 'src/Store'
import { getLayerGroupName } from 'src/utils/Utils'
import { mapActions } from '../../actions/MapActions'
import LayerLegend from './layerlegend/LayerLegend'
import LayerParameters from './layerparameters/LayerParameters'
import { getLayerDescriptionForType, getLayersByDataTheme, getLayerGroups, LayerGroup, LayerType, LayerParamsObject, LayerParams } from 'src/dataSources/layers/LayerRegistry'
import { DataTheme, LayerViewType, ToolType } from 'src/Enums'
import { tooltipDelay } from 'src/Constants'
import { generalActions } from 'src/actions/GeneralActions'
import { profileActions } from 'src/actions/ProfileActions'
import { boreholeInfoActions } from 'src/actions/BoreholeInfoActions'
import { virtualBoreholeInfoActions } from 'src/actions/VirtualBoreholeInfoActions'
import { infoMenuActions } from 'src/actions/InfoMenuActions'
import { ProfileObject } from 'src/dataSources/geo/CrossSection'
import { NearestBoreholeObject } from 'src/dataSources/geo/NearestBoreHoles'

import Confirm from '../confirm/Confirm'

import { layerPickerStyle } from './LayerPicker.style'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import { wikiActions } from 'src/actions/WikiActions'
import { InfoView } from 'src/reducers/InfoMenuReducer'
import { getGraphDescriptionForType, getGraphGroupDescriptionForType, getGraphGroups, Graph, GraphGroup } from 'src/dataSources/graphs/GraphRegistry'
import { getLayerParamsFromType } from 'src/reducers/MapReducer'
import { resetSelectedLayers } from 'src/utils/MapLayerUtils'

/**
 * LayerActiveView represent the current view type being displayed in the layer picker.
 */
export interface LayerActiveView {
  type: LayerType
  view: LayerViewType
}

export interface LayerPickerProps {
  visible: boolean
}

const defaultProps: LayerPickerProps = {
  visible: true
}

/**
 * LayerPicker React component.
 * The following properties are implemented:
 * visible {boolean}: If true the component is visible.
 * @param props React properties object.
 * @returns React view component.
 */
export const LayerPicker: FC<LayerPickerProps> = (props: LayerPickerProps) => {
  const dispatch = useDispatch()
  const classes = layerPickerStyle()
  const intl = useIntl()

  const [currentGraph, setCurrentGraph] = React.useState<Graph>()
  const [showWarning, setShowWarning] = React.useState<boolean>(false)

  const enabledGraphGroups: GraphGroup[] = useSelector((state: RootState): GraphGroup[] => state.generalState.enabledGraphGroups)
  const enabledLayers: LayerType[] = useSelector((state: RootState): LayerType[] => state.mapState.enabledLayers)
  const enabledLayerGroups: LayerGroup[] = useSelector((state: RootState): LayerGroup[] => state.mapState.enabledLayerGroups)
  const layerActiveViews: LayerActiveView[] = useSelector((state: RootState): LayerActiveView[] => state.mapState.layerActiveViews)
  const layerParams = useSelector((state: RootState): LayerParams[] => state.mapState.layerParams)
  const currentDataTheme: DataTheme = useSelector((state: RootState): DataTheme => state.generalState.dataTheme)
  const selectedBoreholeInfo: NearestBoreholeObject | undefined = useSelector((state: RootState): NearestBoreholeObject | undefined => state.boreholeInfo.selectedBorehole)
  const selectedGeoModel: GeoModel | undefined = useSelector((state: RootState): GeoModel | undefined => state.generalState.selectedGeoModel)
  const selectedProfile: ProfileObject | undefined = useSelector((state: RootState): ProfileObject | undefined => state.profileState.selectedProfile)
  const selectedVirtualBorehole: string = useSelector((state: RootState): string => state.virtualBoreholeInfoState.virtualBoreholePng)

  const graphGroups: GraphGroup[] = getGraphGroups(currentDataTheme)
  const layerTypes: LayerType[] = getLayersByDataTheme(currentDataTheme)
  const layerGroups: LayerGroup[] = getLayerGroups(currentDataTheme)

  /**
   * Determine if a given layer type has settings.
   * @returns LayerParamsObject
   */
  const hasParameters = (layerType: LayerType): boolean => {
    if (layerType !== undefined) {
      const params: LayerParamsObject | undefined = getLayerParamsFromType(layerType, layerParams)
      return params !== undefined && (
        params.depth !== undefined ||
        params.level !== undefined ||
        params?.radioSubLayers !== undefined ||
        params?.switchSubLayers !== undefined ||
        params?.subLayers !== undefined
      )
    }
    return false
  }

  /**
   * Does a given group have enabled layers?
   * @param group Group to check.
   * @returns true if layers are enabled, false otherwise.
   */
  const hasEnabledLayers = (group: LayerGroup): boolean => {
    return enabledLayers.some(layer => getLayerDescriptionForType(layer).group === group)
  }

  /**
   * Is a given layer a background layer?
   * @param layerType Layer to check.
   * @returns true if layer is background layer, false otherwise.
   */
  const isBackgroundLayer = (layerType: LayerType): boolean => {
    return getLayerDescriptionForType(layerType).group === LayerGroup.BACKGROUND_MAPS
  }

  /**
   * Is a given layer's legend enabled?
   * @param layerType Layer to check.
   * @returns true if legend is enabled, false otherwise.
   */
  const isLayerLegendEnabled = (layerType: LayerType): boolean => {
    if (selectedGeoModel === undefined && getLayerDescriptionForType(layerType).imageLegend === undefined) {
      return false
    }
    return getLayerDescriptionForType(layerType).legendEnabled
  }

  /**
   * Is a given layer's info visible?
   * @param layerType Layer to check.
   * @returns true if layer info are visible, false otherwise.
   */
  const isLayerInfoVisible = (layerType: LayerType): boolean => {
    if (!isLayerSelected(layerType)) {
      return false
    }
    return layerActiveViews.some(layerView => layerView.type === layerType && layerView.view === LayerViewType.INFO)
  }

  /**
   * Is a given layer's opacity visible?
   * @param layerType Layer to check.
   * @returns true if layer opacity are visible, false otherwise.
   */
  const isLayerOpacityVisible = (layerType: LayerType): boolean => {
    if (!isLayerSelected(layerType)) {
      return false
    }
    return layerActiveViews.some(layerView => layerView.type === layerType && layerView.view === LayerViewType.OPACITY)
  }

  /**
   * Is a given layer's settings visible?
   * @param layerType Layer to check.
   * @returns true if layer settings are visible, false otherwise.
   */
  const isLayerSettingsVisible = (layerType: LayerType): boolean => {
    if (!isLayerSelected(layerType)) {
      return false
    } if (!hasParameters(layerType)) {
      return false
    }
    return layerActiveViews.some(layerView => layerView.type === layerType && layerView.view === LayerViewType.SETTINGS)
  }

  /**
   * Is a given layer selected?
   * @param layerType Layer to check.
   * @returns true if layer is selected, false otherwise.
   */
  const isLayerSelected = (layerType: LayerType): boolean => {
    return enabledLayers.some(selectedLayerType => selectedLayerType === layerType)
  }

  /**
   * Is a given layer group selected?
   * @param graphGroup Layer group to check.
   * @returns true if layer group is selected, false otherwise.
   */
  const isGraphGroupSelected = (graphGroup: GraphGroup): boolean => {
    return enabledGraphGroups.some(selectedGraphGroup => selectedGraphGroup === graphGroup)
  }

  /**
   * Is a given layer group selected?
   * @param layerGroup Layer group to check.
   * @returns true if layer group is selected, false otherwise.
   */
  const isLayerGroupSelected = (layerGroup: LayerGroup): boolean => {
    return enabledLayerGroups.some(selectedLayerGroup => selectedLayerGroup === layerGroup)
  }

  /**
   * Is a given layer's opacity setting enabled?
   * @param layerType Layer to check.
   * @returns true if opacity is enabled, false otherwise.
   */
  const isOpacityEnabled = (layerType: LayerType): boolean => {
    return getLayerDescriptionForType(layerType).opacityEnabled
  }

  /**
   * Called when a layer is clicked.
   * @param layerType Layer that was clicked.
   */
  const layerClicked = (layerType: LayerType): void => {
    const layerDescription = getLayerDescriptionForType(layerType)

    if (layerDescription.group === LayerGroup.BACKGROUND_MAPS) {
      // Disable deselecting a background layer if is already selected
      if (isLayerSelected(layerType)) {
        return
      }

      // If background layer is selected deselect previously selected background layer and remove from context
      for (const enabledLayer of enabledLayers) {
        const enabledLayerDescription = getLayerDescriptionForType(enabledLayer)
        if (enabledLayerDescription.group === LayerGroup.BACKGROUND_MAPS) {
          dispatch(mapActions.removeLayer(enabledLayer))
          dispatch(wikiActions.removeContextTags(enabledLayerDescription.contextTags))
        }
      }
    }

    const layerAction = isLayerSelected(layerType) ? mapActions.removeLayer(layerType) : mapActions.addLayer(layerType)
    dispatch(layerAction)

    const contextTags = layerDescription.contextTags
    const tagAction = isLayerSelected(layerType) ? wikiActions.removeContextTags(contextTags) : wikiActions.addContextTags(contextTags)
    dispatch(tagAction)
  }

  /**
   * Called when a layer groups is clicked.
   * @param group Group that was clicked.
   */
  const selectGroup = (group: LayerGroup): void => {
    if (isLayerGroupSelected(group)) {
      dispatch(mapActions.removeLayerGroup(group))
    } else {
      dispatch(mapActions.addLayerGroup(group))
    }
  }

  /**
   * Called when a graph groups is clicked.
   * @param group Group that was clicked.
   */
  const selectGraphGroup = (group: GraphGroup): void => {
    if (isGraphGroupSelected(group)) {
      dispatch(generalActions.removeGraphGroup(group))
    } else {
      dispatch(generalActions.addGraphGroup(group))
    }
  }

  /**
   * Show graph click handler.
   */
  const showGraphClicked = (graph: Graph): void => {
    if (selectedProfile !== undefined || selectedBoreholeInfo !== undefined || selectedVirtualBorehole !== '') {
      setCurrentGraph(graph)
      setShowWarning(true)
    } else {
      setCurrentGraph(undefined)
      dispatch(infoMenuActions.currentView(InfoView.Empty))
      dispatch(wikiActions.clearViews())
      dispatch(boreholeInfoActions.clearState())
      dispatch(profileActions.clearState())
      dispatch(virtualBoreholeInfoActions.clearState())
      dispatch(generalActions.setSelectedTool(ToolType.INFO))
      dispatch(generalActions.setShowLeftSideBar(false))
      dispatch(generalActions.setSelectedGraph(graph))
    }
  }

  /**
   * Called when the layer info icon is clicked.
   * @param layerType Layer whose info icon was clicked.
   */
  const toggleLayerInfo = (layerType: LayerType): void => {
    if (isLayerInfoVisible(layerType)) {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.NONE))
    } else {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.INFO))
    }
  }

  /**
   * Called when the layer opacity icon is clicked.
   * @param layerType Layer whose opacity icon was clicked.
   */
  const toggleLayerOpacity = (layerType: LayerType): void => {
    if (isLayerOpacityVisible(layerType)) {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.NONE))
    } else {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.OPACITY))
    }
  }

  /**
   * Called when the layer params icon is clicked.
   * @param layerType Layer whose param icon was clicked.
   */
  const toggleLayerParams = (layerType: LayerType): void => {
    if (isLayerSettingsVisible(layerType)) {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.NONE))
    } else {
      dispatch(mapActions.setLayerActiveView(layerType, LayerViewType.SETTINGS))
    }
  }

  /**
   * Called when the collapse/reset icon is pressed.
   * Disables all selected layers, collapses all layer groups and updates WikiContext.
   */
  const handleReset = (): void => {
    resetSelectedLayers(enabledLayers, dispatch)
  }

  // Generate group layers
  const groups: JSX.Element[] = []
  layerGroups.forEach((layerGroup, index) => {
    const group = (
      <div key={index}>
        <ListItem
          button
          className={`${hasEnabledLayers(layerGroup) ? classes.groupWithSelectedLayer : ''} ${index > 0 ? classes.groupWithLayers : ''}`}
          onClick={() => { selectGroup(layerGroup) }}
        >
          <ListItemText primary={getLayerGroupName(layerGroup)} />
          {isLayerGroupSelected(layerGroup) ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={isLayerGroupSelected(layerGroup)} timeout='auto' unmountOnExit>
          <List component='div' disablePadding dense>
            {layerTypes.filter(layerType => getLayerDescriptionForType(layerType).group === layerGroup).map((layerType, index) => {
              return (
                <div key={index}>
                  <ListItem button className={classes.nested} onClick={() => { layerClicked(layerType) }}>
                    <ListItemIcon className={classes.listItemIcon}>
                      {!isBackgroundLayer(layerType) && (
                        <Checkbox
                          className={isLayerSelected(layerType) ? classes.checkbox : ''}
                          edge='start'
                          checked={isLayerSelected(layerType)}
                          tabIndex={-1}
                          disableRipple
                          color='default' // color is set to 'default' to let className define the style
                        />
                      )}
                      {isBackgroundLayer(layerType) && (
                        <Radio
                          className={isLayerSelected(layerType) ? classes.checkbox : ''}
                          edge='start'
                          checked={isLayerSelected(layerType)}
                          tabIndex={-1}
                          disableRipple
                          color='default'
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={intl.formatMessage({ id: getLayerDescriptionForType(layerType).name })} className={classes.listTitle} />
                    {isLayerSelected(layerType) && (
                      <ListItemSecondaryAction>
                        {isLayerLegendEnabled(layerType) && (
                          <Tooltip
                            title={intl.formatMessage({ id: 'layerpicker.info' })}
                            enterDelay={tooltipDelay}
                            placement='right'
                          >
                            <IconButton edge='end' aria-label='info' onClick={() => { toggleLayerInfo(layerType) }}>
                              <InfoOutlined className={isLayerInfoVisible(layerType) ? classes.selectedLayerParamsActive : classes.selectedLayerParams} />
                            </IconButton>
                          </Tooltip>)}
                        {hasParameters(layerType) && (
                          <Tooltip
                            title={intl.formatMessage({ id: 'layerpicker.settings' })}
                            enterDelay={tooltipDelay}
                            placement='right'
                          >
                            <IconButton edge='end' aria-label='settings' onClick={() => { toggleLayerParams(layerType) }}>
                              <TuneOutlined className={isLayerSettingsVisible(layerType) ? classes.selectedLayerParamsActive : classes.selectedLayerParams} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isOpacityEnabled(layerType) && (
                          <Tooltip
                            title={intl.formatMessage({ id: 'layerpicker.opacity' })}
                            enterDelay={tooltipDelay}
                            placement='right'
                          >
                            <IconButton edge='end' aria-label='opacity' onClick={() => { toggleLayerOpacity(layerType) }}>
                              <Opacity className={isLayerOpacityVisible(layerType) ? classes.selectedLayerParamsActive : classes.selectedLayerParams} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                  <Collapse in={isLayerLegendEnabled(layerType) && isLayerInfoVisible(layerType)} timeout='auto' unmountOnExit>
                    <div className={`${classes.triangle} ${hasParameters(layerType) ? classes.thirdTriangle : classes.secondTriangle}`} />
                    <List component='div' disablePadding dense>
                      <ListItemText>
                        <LayerLegend
                          layerType={layerType}
                          imageLegend={getLayerDescriptionForType(layerType).imageLegend}
                        />
                      </ListItemText>
                    </List>
                  </Collapse>
                  <Collapse in={isLayerSettingsVisible(layerType)} timeout='auto' unmountOnExit>
                    <div className={`${classes.triangle} ${classes.secondTriangle}`} />
                    <List component='div' disablePadding dense>
                      <ListItemText>
                        <LayerParameters layerType={layerType} opacitySliderMode={false} closeHandler={() => { toggleLayerParams(layerType) }} />
                      </ListItemText>
                    </List>
                  </Collapse>
                  <Collapse in={isLayerOpacityVisible(layerType)} timeout='auto' unmountOnExit>
                    <div className={`${classes.triangle} ${classes.firstTriangle}`} />
                    <List component='div' disablePadding dense>
                      <ListItemText>
                        <LayerParameters layerType={layerType} opacitySliderMode closeHandler={() => { toggleLayerOpacity(layerType) }} />
                      </ListItemText>
                    </List>
                  </Collapse>
                </div>
              )
            })}
          </List>
        </Collapse>
      </div>
    )
    groups.push(group)
  })
  const graphs: JSX.Element[] = []
  graphGroups.forEach((graphGroup, index) => {
    const graphGroupDescription = getGraphGroupDescriptionForType(graphGroup)
    const graph = (
      <div key={index}>
        <ListItem
          button
          className={index > 0 ? classes.groupWithLayers : ''}
          onClick={() => { selectGraphGroup(graphGroup) }}
        >
          <ListItemText primary={intl.formatMessage({ id: graphGroupDescription.name })} />
          {isGraphGroupSelected(graphGroup) ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={isGraphGroupSelected(graphGroup)} timeout='auto' unmountOnExit>
          <div className={classes.graphsDescription}>
            <FormattedMessage id={graphGroupDescription.description} />
          </div>
          {graphGroupDescription.graphs.map((graph, index) => {
            const graphDescription = getGraphDescriptionForType(graph)
            return (
              <Button key={index} variant='contained' color='secondary' className={classes.graphsButton} onClick={() => { showGraphClicked(graphDescription.type) }}>
                <FormattedMessage id={graphDescription.name} />
              </Button>
            )
          })}
        </Collapse>
      </div>
    )
    graphs.push(graph)
  })
  return (
    <div className={classes.root}>
      <Confirm
        open={showWarning}
        text={intl.formatMessage({ id: 'layerpicker.graphs.warning' })}
        handleConfirm={() => {
          setShowWarning(false)
          if (currentGraph !== undefined) {
            showGraphClicked(currentGraph)
          }
        }}
        handleCancel={() => {
          setShowWarning(false)
        }}
      />
      <List
        component='nav'
        aria-labelledby='nested-list-subheader'
        subheader={
          <ListSubheader component='div' className={classes.nestedListSubheader} id='nested-list-subheader'>
            <div className={classes.nestedListSubheaderIcon}>
              <Layers />
            </div>
            <FormattedMessage id='layerpicker.title' />
            <Tooltip title={intl.formatMessage({ id: 'layerpicker.reset' })} enterDelay={tooltipDelay} placement='left'>
              <IconButton className={classes.collapseButton} onClick={handleReset}>
                <VerticalAlignCenterOutlined />
              </IconButton>
            </Tooltip>
          </ListSubheader>
        }
        className={classes.list}
        style={{ display: props.visible ? '' : 'none' }}
      >
        <div className={classes.groups}>
          {groups}
        </div>
      </List>
      {graphs.length > 0 && (
        <List
          component='nav'
          aria-labelledby='nested-list-subheader'
          subheader={
            <ListSubheader component='div' className={classes.nestedListSubheader} id='nested-list-subheader'>
              <FormattedMessage id='layerpicker.graphs' />
              <div className={classes.nestedListSubheaderIcon}>
                <ShowChart />
              </div>
            </ListSubheader>
          }
          className={classes.graphs}
          style={{ display: props.visible ? '' : 'none' }}
        >
          {graphs}
        </List>
      )}
    </div>
  )
}

LayerPicker.defaultProps = defaultProps

export default LayerPicker
